import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { finalize } from 'rxjs';
import { tryGetErrorMessage } from '../../../../../error.utils';
import { LossPreventionService } from '../../../../../service/loss-prevention.service';
import { NtfReasonCategory, NtfReasonOption } from '../../../../../types';
import { ReviewFormService } from '../review-form.service';

const OTHER_NTF_REASON_CATEGORY = 'Other';
const OTHER_NTF_REASON_OPTION: NtfReasonOption = { label: 'Other', category: OTHER_NTF_REASON_CATEGORY };

@Component({
  selector: 'app-review-form-ntf-reason',
  templateUrl: './review-form-ntf-reason.component.html',
  styleUrl: './review-form-ntf-reason.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ReviewFormNtfReasonComponent implements OnInit {
  private lossPreventionService = inject(LossPreventionService);
  private reviewFormService = inject(ReviewFormService);

  public form = input.required<FormGroup>();
  public readonly reasonOptions = signal<NtfReasonOption[]>([]);
  public readonly isLoadingData = signal<boolean>(false);
  public readonly errorMessage = signal<string | undefined>(undefined);
  public readonly showCommentsField = computed(() => this.reviewFormService.ntfReason()?.category === OTHER_NTF_REASON_CATEGORY);

  public ngOnInit(): void {
    this.refresh();
  }

  public refresh(): void {
    this.isLoadingData.set(true);
    this.errorMessage.set(undefined);
    this.lossPreventionService
      .getNtfReasons()
      .pipe(finalize(() => this.isLoadingData.set(false)))
      .subscribe({
        next: (categories) => {
          const options = this.toReasonOptions(categories);
          this.reasonOptions.set(options);

          const comments = this.form().get('comments')?.value?.trim();
          const ntfReason = this.form().get('ntfReason');
          if (!comments || !ntfReason || ntfReason.value) return;

          const option = options.find((reason) => reason.label === comments);
          ntfReason.setValue(option ?? OTHER_NTF_REASON_OPTION);
        },
        error: (err) => {
          this.reasonOptions.set(this.toReasonOptions());
          this.errorMessage.set(tryGetErrorMessage(err));
        },
      });
  }

  private toReasonOptions(categories: NtfReasonCategory[] = []): NtfReasonOption[] {
    // lp api is expected to already sort this by "sort_order"
    const reasons = categories.flatMap((cat) => cat.reasons.map((r) => ({ label: r.label, category: cat.label })));
    // "other" option must always be available!! (nice try santiago)
    return [...reasons, OTHER_NTF_REASON_OPTION];
  }
}
